import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Tabs } from "../../components/Tabs"
import { Tab } from "../../components/Tabs/Tab"
import { MeusTickets, TicketTabId } from "./MeusTickets";
import { ColumnSort, Ticket, TicketRequest } from "../../types/tickets/types";
import { useUserStore } from "../../store/user";
import { useTicketStore } from "../../store/ticket";
import { useExportTicketsAtendimento, useExportTicketsDesenvolvimento, useExportTicketsHistoricoTransacao, useExportTicketsOrdemServico, useTicketsAtendimento, useTicketsDesenvolvimento, useTicketsHistoricoTransacao, useTicketsOrdemServico } from "../../services/tickets/useTickets";
import { TicketResponse } from "../../types/tickets/types";
import { useAuth } from "../../contexts/AuthContext";
import { LoadingOverlay } from "../../components/Loading";
import { NotFound } from "../../components/NotFound";
import { SortDirection } from "../../components/Table/types";
import { useFetchContatos } from "../../services/clientes/useClientes";
import { Contact } from "../../types/cliente/contatos/types";
import { AttachExtranetFilesParams } from "../../types/atendimentos/pre-atendimento/types";
import { useShowNotification } from "../../hooks/useShowNotificationContext";
import { useAnexarArquivoIntranet } from "../../services/atendimentos/useAtendimentos";
import { ModalAttach } from "./ModalAttach/ModalAttach";
import { Action } from "./Actions";
import { ModalDirectChannelContainer } from "./ModalDirectChannel/ModalDirectChannelContainer";
import { ModalPriorityContainer } from "./ModalPriority/ModalPriorityContainer";
import { useTicketFiltersStore } from "../../store/ticketFilters";
import { useTicketActiveTab } from "../../store/ticketsActiveTab";
import { ModalViewAttachmentsContainer } from "./ModaViewAttachments/ModalViewAttachmentsContainer";

export enum ModalType {
    ATTACH_FILE,
    PRIORITY,
    DIRECT_CHANNEL,
    VIEW_ATTACHMENTS
}

export const MeusTicketsContainer = () => {
    const { oidCliente } = useAuth()

    const user = useUserStore((state) => state.user);
    const { setTicket, clearTicket, ticket } = useTicketStore();

    const navigate = useNavigate();


    const [showModal, setShowModal] = useState<boolean | ModalType>(false)
    const [attachFilesParams, setAttachFilesParams] = useState<AttachExtranetFilesParams>()
    const [files, setFiles] = useState<File[]>([]);

    const { filters, setFilters, updateFilters } = useTicketFiltersStore();
    const { activeTab, setActiveTab } = useTicketActiveTab();

    useEffect(() => {
        if (!user || !oidCliente) return;

        if (filters && filters.oidCliente === oidCliente) return;

        setFilters({
            isMaster: user.isMaster,
            oidCliente,
            pagina: 1,
            tamanhoPagina: 10,
            situacao: 'todos',
        });
    }, [user, oidCliente, filters, setFilters]);



    const { showSucessNotification, showErrorNotification } = useShowNotification()
    const anexarArquivoIntranet = useAnexarArquivoIntranet();


    const { data: contactsData } = useFetchContatos(
        oidCliente ?? 0,
        {
            enabled: !!oidCliente,
        }
    );

    const { data: atendimentoData, refetch: atendimentoRefetch,
        isLoading: atendimentoLoading, isError: atendimentoError,
        error: atendimentoErrorData } = useTicketsAtendimento(
            filters!,
            {
                enabled: !!filters && activeTab === 0,
            }
        );

    const { data: desenvolvimentoData, refetch: desenvolvimentoRefetch,
        isLoading: desenvolvimentoLoading, isError: desenvolvimentoError,
        error: desenvolvimentoErrorData } = useTicketsDesenvolvimento(
            filters!,
            {
                enabled: activeTab === 1 && !!filters,
            }
        );

    const { data: ordemServicoData, refetch: ordemServicoRefetch,
        isLoading: ordemServicoLoading, isError: ordemServicoError,
        error: ordemServicoErrorData } = useTicketsOrdemServico(
            filters!,
            {
                enabled: activeTab === 2 && !!filters,
            }
        );

    const { data: historicoTransacaoData, refetch: historicoTransacaoRefetch,
        isLoading: historicoTransacaoLoading, isError: historicoTransacaoError,
        error: historicoTransacaoErrorData } = useTicketsHistoricoTransacao(
            filters!,
            {
                enabled: activeTab === 3 && !!filters,
            }
        );

    const refetchFunctions = [
        atendimentoRefetch,
        desenvolvimentoRefetch,
        ordemServicoRefetch,
        historicoTransacaoRefetch,
    ];

    const isLoading = atendimentoLoading || desenvolvimentoLoading || ordemServicoLoading || historicoTransacaoLoading;
    const isError = atendimentoError || desenvolvimentoError || ordemServicoError || historicoTransacaoError;
    const error = atendimentoErrorData || desenvolvimentoErrorData || ordemServicoErrorData || historicoTransacaoErrorData;

    const handleTab = (newValue: number) => {
        setActiveTab(newValue);
    };

    const handleSelectedStatus = (data: TicketRequest) => {
        updateFilters({
            ...data,
            pagina: 1,
        });
    };

    const handleItemsPerPageChange = (itemsPerPage: number) => {
        updateFilters({
            tamanhoPagina: itemsPerPage,
            pagina: 1,
        });
    };

    const handlePageChange = (page: number) => {
        updateFilters({ pagina: page });
    };

    const handleFilter = (data: TicketRequest) => {
        setFilters({ ...data, pagina: 1 });
    };

    const onSelectGetHistory = (ticket: Ticket, event: React.MouseEvent<HTMLButtonElement>) => {
        setTicket(ticket);
        if (event.ctrlKey || event.metaKey) {
            window.open(`/tickets/${ticket.ticket}`, '_blank');
        } else {
            navigate(`/tickets/${ticket.ticket}`);
        }
    };

    const onSelectFeedback = (ticket: Ticket) => {
        navigate(`/tickets/feedback/${ticket.referenciaExterna}`);
    }

    const onSort = (key: string, direction: SortDirection) => {
        updateFilters({
            campoOrdenado: ColumnSort[key.toUpperCase() as keyof typeof ColumnSort],
            sentidoOrdenacao: direction,
        });
    };

    const downloadFile = (blob: Blob, filename: string) => {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    const onAction = (type: Action, ticket: Ticket, event?: React.MouseEvent<HTMLButtonElement>) => {
        switch (type) {
            case Action.Feedback:
                onSelectFeedback(ticket);
                break;
            case Action.History:
                onSelectGetHistory(ticket, event!);
                break;
            case Action.Attach:
                onSelectAttach(ticket);
                break;
            case Action.Priority:
                onSelectPriority(ticket);
                break;
            case Action.Direct_channel:
                onSelectDirectChannel(ticket);
                break;
        }
    }

    const onRemoveFile = (index: number) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const onAttachFiles = (items: { base64: string; name: string }[]) => {
        onSendFiles(items);
    };


    const onSelectPriority = (ticket: Ticket) => {
        setTicket(ticket);
        setShowModal(ModalType.PRIORITY);
    }

    const onSelectDirectChannel = (ticket: Ticket) => {
        setTicket(ticket);
        setShowModal(ModalType.DIRECT_CHANNEL);
    }

    const onSelectAttach = (ticket: Ticket) => {
        setTicket(ticket);
        setShowModal(ModalType.ATTACH_FILE);
    }

    const onSelectViewAttachments = (ticket: Ticket) => {
        setTicket(ticket);
        setShowModal(ModalType.VIEW_ATTACHMENTS);
    }

    const onCloseModalAttach = () => {
        setShowModal(false);
        setAttachFilesParams({})
        setFiles([]);
        clearTicket();
    }

    const onCloseModalPriority = () => {
        setShowModal(false);
        clearTicket();
    }

    const onConfirmModalPriority = () => {
        atendimentoRefetch();
        setShowModal(false);
        clearTicket();
    }

    const onCloseModalDirectChannel = () => {
        setShowModal(false);
        clearTicket();
    }

    const onSendFiles = async (items: { base64: string; name: string }[]) => {
        if (!ticket || !items?.length) return;

        let successes = 0;
        let failures = 0;

        for (const item of items) {
            const extensionFile = item.name.split(".").pop() || "";
            const baseName = item.name.replace(/\.[^/.]+$/, "");

            const payload = {
                ...attachFilesParams,
                numeroFicha: ticket.ticket,
                arquivo: item.base64,
                nomeArquivo: baseName,
                referencia: baseName,
                tipoArquivo: extensionFile,
            };

            try {
                await anexarArquivoIntranet.mutateAsync(payload);
                successes++;
            } catch (e) {
                console.error(e);
                failures++;
                showErrorNotification(e as any, `Falha ao anexar "${item.name}".`);
            }
        }

        if (successes > 0) {
            showSucessNotification(
                successes === 1
                    ? "Arquivo anexado com sucesso!"
                    : `${successes} arquivos anexados com sucesso!`
            );
        }

        if (failures === 0) {
            setShowModal(false);
            setAttachFilesParams({});
            setFiles([]);
            clearTicket();
        }
    };



    const exportMutations = {
        [TicketTabId.ATENDIMENTOS]: useExportTicketsAtendimento(),
        [TicketTabId.DESENVOLVIMENTO]: useExportTicketsDesenvolvimento(),
        [TicketTabId.ORDEM_SERVICO]: useExportTicketsOrdemServico(),
        [TicketTabId.HISTORICO_TRANSACAO]: useExportTicketsHistoricoTransacao(),
    };

    const onExportClick = (tabId: TicketTabId) => {
        const mutation = exportMutations[tabId];

        if (!mutation) return;

        mutation.mutate(filters!, {
            onSuccess: (blob) => {
                let filename = "relatorio.xlsx";
                switch (tabId) {
                    case TicketTabId.ATENDIMENTOS:
                        filename = "atendimentos.xlsx";
                        break;
                    case TicketTabId.DESENVOLVIMENTO:
                        filename = "desenvolvimento.xlsx";
                        break;
                    case TicketTabId.ORDEM_SERVICO:
                        filename = "ordem-servico.xlsx";
                        break;
                    case TicketTabId.HISTORICO_TRANSACAO:
                        filename = "historico-transacoes.xlsx";
                        break;
                }
                downloadFile(blob, filename);
            },
            onError: (error) => {
                console.error("Erro ao exportar:", error);
            },
        });
    };

    const renderMeusTicketsTab = (data: TicketResponse | undefined, contatcts: Contact[] | undefined, tabId: TicketTabId) => {
        return data && contatcts && filters && (
            <MeusTickets
                data={data}
                filters={filters}
                contacts={contatcts}
                tabId={tabId}
                onSelectedStatus={handleSelectedStatus}
                onItemsPerPageChange={handleItemsPerPageChange}
                onPageChange={handlePageChange}
                onFilter={handleFilter}
                onAction={onAction}
                onSort={onSort}
                onExportClick={() => onExportClick(tabId)}
                onPriorityClick={() => setShowModal(ModalType.PRIORITY)}
                onSelectViewAttachments={onSelectViewAttachments}
            />
        );
    };

    const tabsConfig = [
        { label: "atendimentos", data: atendimentoData, id: TicketTabId.ATENDIMENTOS },
        { label: "desenvolvimento", data: desenvolvimentoData, id: TicketTabId.DESENVOLVIMENTO },
        { label: "ordem de serviço", data: ordemServicoData, id: TicketTabId.ORDEM_SERVICO },
        { label: "histórico de transações", data: historicoTransacaoData, id: TicketTabId.HISTORICO_TRANSACAO },
    ];

    return (

        <>
            {isError ? (
                <NotFound
                    error={error}
                    defaultMessage={'Erro ao carregar a lista de tickets'}
                    retry={refetchFunctions[activeTab]}
                />
            )
                :
                (
                    <>
                        {isLoading && <LoadingOverlay />}

                        {<div style={{ width: "100%", alignSelf: "center" }}>
                            <Tabs value={activeTab} onChange={handleTab}>
                                {tabsConfig.map((tab, index) => (
                                    <Tab key={index} label={tab.label} index={index}>
                                        {renderMeusTicketsTab(tab.data, contactsData, tab.id)}
                                    </Tab>
                                ))}
                            </Tabs>
                        </div>
                        }
                    </>
                )
            }

            {showModal === ModalType.ATTACH_FILE &&
                <ModalAttach
                    isOpen={showModal === ModalType.ATTACH_FILE}
                    files={files}
                    onClose={onCloseModalAttach}
                    onFilesChange={setFiles}
                    onRemoveFile={onRemoveFile}
                    onClearAll={() => setFiles([])}
                    onAttachFiles={onAttachFiles}
                />
            }

            {showModal === ModalType.PRIORITY &&
                <ModalPriorityContainer
                    isOpen={showModal === ModalType.PRIORITY}
                    onClose={onCloseModalPriority}
                    onConfirm={onConfirmModalPriority}
                />
            }

            {showModal === ModalType.DIRECT_CHANNEL &&
                <ModalDirectChannelContainer
                    isOpen={showModal === ModalType.DIRECT_CHANNEL}
                    onClose={onCloseModalDirectChannel}
                />
            }

            {showModal === ModalType.VIEW_ATTACHMENTS &&
                <ModalViewAttachmentsContainer
                    isOpen={showModal === ModalType.VIEW_ATTACHMENTS}
                    onClose={onCloseModalDirectChannel}
                />
            }
        </>
    );
};